import { CcfBox, CcfTypography } from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-interactions-list-view.module.scss';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import CcfInteractionsCard, {
  CcfInteractionsCardProps,
} from '../ccf-interactions-card/ccf-interactions-card';
import { makeStyles } from '@mui/styles';
import { useCallback, useMemo } from 'react';
import { interactionsHistory } from '../mock-data/interactions-history-mock-data';

/* eslint-disable-next-line */
export interface CcfConsumerCardInteractionsListViewProps {
  interactionsHistoryData: any;
  interactionViewSelectHandler: (view: string, interaction: any) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '90%',
    overflowY: 'auto',
    '& .MuiTimelineItem-missingOppositeContent:before': {
      flex: 'none',
      padding: 0,
    },
    '& .MuiTimeline-root': {
      margin: 0,
      padding: '10px',
    },
    '& .MuiTimelineItem-root': {
      minHeight: 'auto',
    },
  },
  lastTimelineItem: {
    minHeight: 'auto',
  },
}));

/**
 * CcfConsumerCardInteractionsListView - returns Interactions Tab List View
 * @param props - CcfConsumerCardInteractionsListViewProps
 * @returns - appspace
 * @example - `<CcfConsumerCardInteractionsListView />`
 */
export function CcfConsumerCardInteractionsListView(
  props: CcfConsumerCardInteractionsListViewProps
) {
  const classes = useStyles();
  const { interactionsHistoryData, interactionViewSelectHandler } = props;

  const groupInteractionsByDate = useMemo(() => {
    /**
     * Function to group the interactions by Date
     * @example groupInteractionsByDate()
     */
    const groupInteractionsByDateHandler = (interactions: any) => {
      return interactions.reduce((acc: any, interaction: any) => {
        const date = new Date(interaction.date).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(interaction);
        return acc;
      }, {});
    };

    const groupedData = groupInteractionsByDateHandler(interactionsHistoryData);
    const sortedKeys = Object.keys(groupedData).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
    const sortedGroupedData: Record<string, interactionsHistory[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroupedData[key] = groupedData[key];
    });
    return sortedGroupedData;
  }, [interactionsHistoryData]);

  /**
   * Function to select interaction view
   * @example handleInteractionView()
   */
  const handleInteractionView = useCallback(
    (selectedView: string, interaction: any) => {
      interactionViewSelectHandler(selectedView, interaction);
    },
    [interactionViewSelectHandler]
  );

  return (
    <CcfBox className={classes.root}>
      <Timeline>
        {Object.keys(groupInteractionsByDate).map(
          (date: string, index: number) => {
            const group = groupInteractionsByDate[date];
            return group.length > 1 ? (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <CcfTypography className={styles.timelineDate}>
                    {date}
                  </CcfTypography>
                  {group.map((interaction: any) => (
                    <CcfInteractionsCard
                      key={interaction.interactionId}
                      interaction={interaction}
                      onClickInteractionCard={() =>
                        handleInteractionView('Details', interaction)
                      }
                    />
                  ))}
                </TimelineContent>
              </TimelineItem>
            ) : (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <CcfTypography className={styles.timelineDate}>
                    {date}
                  </CcfTypography>
                  <CcfInteractionsCard
                    key={group[0].interactionId}
                    interaction={group[0]}
                    onClickInteractionCard={() =>
                      handleInteractionView('Details', group[0])
                    }
                  />
                </TimelineContent>
              </TimelineItem>
            );
          }
        )}
      </Timeline>
    </CcfBox>
  );
}

export default CcfConsumerCardInteractionsListView;
